import React from 'react';
import { DocumentLineItemWorkOrder } from '../types/Types';

interface UseNestedDocumentKeyboardParams {
  columnOrder: string[];
  hiddenColumns: string[];
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  localRows: DocumentLineItemWorkOrder[];
  setLocalRows: React.Dispatch<React.SetStateAction<DocumentLineItemWorkOrder[]>>;
  createEmptyRow: () => DocumentLineItemWorkOrder;
  handleSaveAllPendingRows: () => Promise<boolean>;
}

/**
 * Keyboard navigation for NestedDocumentTable's grid (Enter to save, arrow-key navigation).
 * Extracted 1:1 from NestedDocumentTable.tsx (CLAUDE.md rule 9 split) — no behaviour change.
 */
export function useNestedDocumentKeyboard({
  columnOrder,
  hiddenColumns,
  inputRefs,
  localRows,
  setLocalRows,
  createEmptyRow,
  handleSaveAllPendingRows,
}: UseNestedDocumentKeyboardParams) {
  // Enhanced keyboard navigation similar to PartnerRoleInfoTable
  const handleTableKeyDown = async (e: React.KeyboardEvent<HTMLElement>, rowIdx: number, colIdx: number) => {
    if (e.isDefaultPrevented()) return;

    // Calculate max columns dynamically based on visible columns + 2 for checkbox and loading
    const maxColumns = columnOrder.filter(field => !hiddenColumns.includes(field)).length + 1; // +1 because we subtract 1 in comparison

    const goTo = (r: number, c: number) => inputRefs.current[r]?.[c]?.focus();

    switch (e.key) {
      case "Enter":
        // Only handle Enter if it's from an input or select element
        const target = e.target as HTMLElement;
        if (!target || (target.tagName !== 'INPUT' && target.tagName !== 'SELECT')) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        console.log('NestedDocumentTable: Enter key pressed, saving rows...');
        // Save all pending rows that have data
        try {
          await handleSaveAllPendingRows();
          console.log('NestedDocumentTable: Save completed successfully');
        } catch (error) {
          console.error('NestedDocumentTable: Save failed:', error);
        }
        break;

      case "ArrowUp":
        if (rowIdx > 0) {
          e.preventDefault();
          goTo(rowIdx - 1, colIdx);
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        const nextRowIndex = rowIdx + 1;
        const currentRow = localRows[rowIdx];
        const isLastRow = rowIdx === localRows.length - 1;

        // Check if current row is empty (has no data)
        const isCurrentRowEmpty = !currentRow._id &&
          !currentRow.item &&
          !currentRow.purchasePrice &&
          !currentRow.quantity &&
          !currentRow.serial &&
          !currentRow.deadline;

        if (!localRows[nextRowIndex]) {
          // Only create new row if current row is NOT empty (has some data)
          if (!isCurrentRowEmpty) {
            setLocalRows(prev => [...prev, createEmptyRow()]);

            setTimeout(() => {
              goTo(nextRowIndex, colIdx);
            }, 0);
          }
          // If current row is empty, don't create another empty row
        } else {
          // Navigate to existing next row
          goTo(nextRowIndex, colIdx);
        }
        break;

      case "ArrowLeft":
        if (colIdx > 0) {
          e.preventDefault();
          goTo(rowIdx, colIdx - 1);
        }
        break;

      case "ArrowRight":
        if (colIdx < maxColumns) {
          e.preventDefault();
          goTo(rowIdx, colIdx + 1);
        }
        break;
    }
  };

  return { handleTableKeyDown };
}

export default useNestedDocumentKeyboard;
